import { useListPagination } from '@/hooks/useListPagination'
import { ListPagination } from '@/components/ui/list-pagination'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useSortingState } from '@/hooks/useSortingState'
import { estraiDipendenze, formatDipendenzeMessage } from '@/utils/deleteProtectedError'
import { SortableTableHead } from '@/components/ui/sortable-table-head'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  TrashIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline'
import { Package } from 'lucide-react'
import { giacenzeComponentiApi } from '@/api/magazzino'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { fetchAllPaginatedResults } from '@/utils/pagination'
import {
  STOCK_FILTER_OPTIONS,
  STOCK_FILTER_VALUES,
} from '@/utils/stockFilters'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { componentiApi } from '@/api/box'

/**
 * Pagina lista componenti scatola.
 * Gestisce fondi, coperchi, fustelle, paglia, nastri.
 */
export default function ComponentiList() {
  const [search, setSearch] = useState('')
  const [filtroTipo, setFiltroTipo] = useState('tutti')
  const [filtroAttivo, setFiltroAttivo] = useState('tutti')
  const [stockFilter, setStockFilter] = useState(STOCK_FILTER_VALUES.ALL)
  const valoreMetrica = 'costo'
  const [ordering, setOrdering] = useSortingState('box-componenti', 'nome')
  const [deleteDialog, setDeleteDialog] = useState({ open: false, componente: null })
  const [stockDialog, setStockDialog] = useState({ open: false, componente: null, giacenza: null })
  const [stockForm, setStockForm] = useState({
    quantita_disponibile: '0',
    soglia_minima: '50',
    ubicazione: '',
  })
  
  const queryClient = useQueryClient()

  // Query componenti
  const { page, setPage } = useListPagination({ search, filtroTipo, filtroAttivo, stockFilter, ordering })

  const { data: componentiData, isLoading, isFetching, error } = useQuery({
    queryKey: ['componenti-scatola', { page, stockFilter, search, tipo: filtroTipo, attivo: filtroAttivo, ordering }],
    queryFn: () => {
      const params = { ordering, page, stock_filter: stockFilter }
      if (search) params.search = search
      if (filtroTipo !== 'tutti') params.tipo = filtroTipo
      if (filtroAttivo !== 'tutti') params.attivo = filtroAttivo === 'attivi'
      return componentiApi.getAll(params)
    },
  })

  // Query giacenze componenti (tutte le pagine) per vista unica anagrafica + stock
  const { data: giacenzeData } = useQuery({
    queryKey: ['giacenze-componenti', 'catalogo-all-pages'],
    queryFn: () => fetchAllPaginatedResults((params) => giacenzeComponentiApi.getAll(params)),
  })

  // Query KPI valore vista corrente componenti (allineato ai filtri lista)
  const [lastValidValoreVista, setLastValidValoreVista] = useState(null)
  const {
    data: valoreVistaData,
    isFetching: isValoreVistaFetching,
    isError: isValoreVistaError,
    refetch: refetchValoreVista,
  } = useQuery({
    queryKey: [
      'giacenze-componenti',
      'valore-vista',
      { search, tipo: filtroTipo, attivo: filtroAttivo, stockFilter, metrica: valoreMetrica, ordering },
    ],
    queryFn: () => {
      const params = {
        metrica: valoreMetrica,
        ordering,
      }
      if (search) params.search = search
      if (filtroTipo !== 'tutti') params.tipo = filtroTipo
      if (filtroAttivo !== 'tutti') params.attivo = filtroAttivo === 'attivi'
      if (stockFilter !== STOCK_FILTER_VALUES.ALL) params.stock_filter = stockFilter
      return giacenzeComponentiApi.valoreVista(params)
    },
  })

  // Mutation eliminazione
  const deleteMutation = useMutation({
    mutationFn: componentiApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['componenti-scatola'] })
      toast.success('Componente eliminato con successo')
      setDeleteDialog({ open: false, componente: null })
    },
    onError: (error) => {
      // Gestione 400 "Impossibile eliminare: oggetto usato altrove" (Punto 5)
      const parsed = estraiDipendenze(error)
      if (parsed) {
        toast.error(formatDipendenzeMessage(parsed))
      } else {
        toast.error('Errore durante l\'eliminazione')
      }
    },
  })

  // Mutation modifica giacenza componente inline
  const updateStockMutation = useMutation({
    mutationFn: ({ giacenzaId, data }) => giacenzeComponentiApi.patch(giacenzaId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giacenze-componenti'] })
      queryClient.invalidateQueries({ queryKey: ['componenti-scatola'] })
      toast.success('Giacenza componente aggiornata con successo')
      setStockDialog({ open: false, componente: null, giacenza: null })
    },
    onError: (error) => {
      const errorData = error.response?.data
      const firstError = errorData ? Object.values(errorData)[0] : null
      toast.error(Array.isArray(firstError) ? firstError[0] : (firstError || 'Errore durante l\'aggiornamento giacenza'))
    },
  })

  const componenti = Array.isArray(componentiData?.data)
    ? componentiData.data
    : (componentiData?.data?.results || [])
  const giacenze = Array.isArray(giacenzeData) ? giacenzeData : []
  const giacenzeByComponente = new Map(
    giacenze.map((g) => [g.componente, g])
  )
  const valoreVistaApi = valoreVistaData?.data
  useEffect(() => {
    if (valoreVistaApi && typeof valoreVistaApi.count === 'number') {
      setLastValidValoreVista(valoreVistaApi)
    }
  }, [valoreVistaApi])
  const valoreVista = valoreVistaApi || lastValidValoreVista
  const quantitaVista = valoreVista?.quantita || { disponibile: 0, riservata: 0, effettiva: 0 }
  const valoreVistaCents = valoreVista?.valore_cents || { disponibile: 0, riservata: 0, effettiva: 0 }
  const isValoreVistaEmpty = (valoreVista?.count ?? 0) === 0
  const componentiFiltrati = componenti
  const hasActiveFilters = Boolean(
    search || filtroTipo !== 'tutti' || filtroAttivo !== 'tutti' || stockFilter !== STOCK_FILTER_VALUES.ALL
  )

  const tipiComponente = [
    { value: 'fondo', label: 'Fondo' },
    { value: 'coperchio', label: 'Coperchio' },
    { value: 'fustella', label: 'Fustella' },
    { value: 'paglia', label: 'Paglia' },
    { value: 'nastro', label: 'Nastro' },
    { value: 'pellicola', label: 'Pellicola' },
    { value: 'sottovuoto', label: 'Sottovuoto' },
    { value: 'bustina', label: 'Bustina' },
    { value: 'coppetta', label: 'Coppetta' },
    { value: 'scatolina', label: 'Scatolina' },
    { value: 'adesivo', label: 'Adesivo' },
    { value: 'porta_lettera_a5', label: 'Porta Lettera A5' },
    { value: 'vasetto', label: 'Vasetto' },
    { value: 'cartone_spedizioni', label: 'Cartone Spedizioni' },
    { value: 'scotch', label: 'Scotch' },
    { value: 'altro', label: 'Altro' },
  ]

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100)
  }

  const formatCurrencyFromCents = (cents = 0) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format((cents || 0) / 100)
  }

  const getTipoBadgeColor = (tipo) => {
    const colors = {
      fondo: 'bg-blue-100 text-blue-800',
      coperchio: 'bg-green-100 text-green-800',
      fustella: 'bg-yellow-100 text-yellow-800',
      paglia: 'bg-orange-100 text-orange-800',
      nastro: 'bg-pink-100 text-pink-800',
      altro: 'bg-gray-100 text-gray-800',
    }
    return colors[tipo] || colors.altro
  }

  const getStockBadge = (giacenza) => {
    if (!giacenza) return <Badge variant="secondary">N/D</Badge>
    if (giacenza.is_sottoscorta) return <Badge variant="destructive">Sottoscorta</Badge>
    return <Badge variant="success">OK</Badge>
  }

  const openStockDialog = (componente) => {
    const giacenza = giacenzeByComponente.get(componente.id)
    if (!giacenza) {
      toast.error('Giacenza non disponibile per questo componente')
      return
    }

    setStockForm({
      quantita_disponibile: String(giacenza.quantita_disponibile ?? 0),
      soglia_minima: String(giacenza.soglia_minima ?? 50),
      ubicazione: giacenza.ubicazione || '',
    })
    setStockDialog({ open: true, componente, giacenza })
  }

  const handleStockSubmit = () => {
    if (!stockDialog.giacenza) return

    const quantitaDisponibile = Number.parseInt(stockForm.quantita_disponibile, 10) || 0
    const quantitaRiservata = stockDialog.giacenza.quantita_riservata || 0

    if (quantitaDisponibile < quantitaRiservata) {
      toast.error(`La quantità disponibile non può essere inferiore alla riservata (${quantitaRiservata})`)
      return
    }

    updateStockMutation.mutate({
      giacenzaId: stockDialog.giacenza.id,
      data: {
        quantita_disponibile: quantitaDisponibile,
        soglia_minima: Number.parseInt(stockForm.soglia_minima, 10) || 0,
        ubicazione: stockForm.ubicazione.trim(),
      },
    })
  }

  const handleResetFilters = () => {
    setSearch('')
    setFiltroTipo('tutti')
    setFiltroAttivo('tutti')
    setStockFilter(STOCK_FILTER_VALUES.ALL)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Componenti Scatola</h1>
          <p className="text-muted-foreground">
            Gestisci fondi, coperchi, fustelle, paglia e nastri
          </p>
        </div>
        <Button asChild>
          <Link to="/box/componenti/nuovo">
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuovo Componente
          </Link>
        </Button>
      </div>

      {/* Statistiche */}
      <div className="grid gap-4 md:grid-cols-6">
        {tipiComponente.map((tipo) => (
          <Card key={tipo.value}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {tipo.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {componenti.filter(c => c.tipo === tipo.value).length}
                <span className="block text-xs font-normal text-muted-foreground">in questa pagina</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            <div className="relative md:col-span-2">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per nome o codice..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger>
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti i tipi</SelectItem>
                {tipiComponente.map((tipo) => (
                  <SelectItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroAttivo} onValueChange={setFiltroAttivo}>
              <SelectTrigger>
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti</SelectItem>
                <SelectItem value="attivi">Attivi</SelectItem>
                <SelectItem value="inattivi">Inattivi</SelectItem>
              </SelectContent>
            </Select>
            <Select value={stockFilter} onValueChange={setStockFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Stock" />
              </SelectTrigger>
              <SelectContent>
                {STOCK_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="md:col-span-5 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleResetFilters}
                disabled={!hasActiveFilters}
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Reset filtri
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <CardTitle className="text-base font-medium">
              Valore economico del magazzino (Componenti)
            </CardTitle>
            <div className="text-sm font-medium text-muted-foreground">
              Valorizzazione al costo di acquisto
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Valore fisicamente disponibile</p>
              {isValoreVistaFetching ? (
                <div className="mt-2 h-12 animate-pulse rounded bg-muted" />
              ) : (
                <>
                  <p className="mt-1 text-2xl font-semibold text-emerald-600">{formatCurrencyFromCents(valoreVistaCents.disponibile)}</p>
                  <p className="text-sm text-muted-foreground">{quantitaVista.disponibile} unità complessive</p>
                </>
              )}
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Valore impegnato negli ordini</p>
              {isValoreVistaFetching ? (
                <div className="mt-2 h-12 animate-pulse rounded bg-muted" />
              ) : (
                <>
                  <p className="mt-1 text-2xl font-semibold text-amber-600">{formatCurrencyFromCents(valoreVistaCents.riservata)}</p>
                  <p className="text-sm text-muted-foreground">{quantitaVista.riservata} unità riservate</p>
                </>
              )}
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Valore libero per nuovi ordini</p>
              {isValoreVistaFetching ? (
                <div className="mt-2 h-12 animate-pulse rounded bg-muted" />
              ) : (
                <>
                  <p className="mt-1 text-2xl font-semibold text-primary">{formatCurrencyFromCents(valoreVistaCents.effettiva)}</p>
                  <p className="text-sm text-muted-foreground">{quantitaVista.effettiva} unità libere</p>
                </>
              )}
            </div>
          </div>
          {!isValoreVistaError && !isValoreVistaFetching && isValoreVistaEmpty && (
            <p className="text-xs text-muted-foreground">
              Nessun elemento nella vista corrente: KPI valorizzati a zero.
            </p>
          )}
          {!isValoreVistaError && !isValoreVistaFetching && !isValoreVistaEmpty && (
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{valoreVista?.count ?? 0}</span> referenze considerate · valori al costo di acquisto
            </p>
          )}
          {!isValoreVistaError && valoreVista?.note && (
            <p className="text-xs text-muted-foreground">{valoreVista.note}</p>
          )}
          {isValoreVistaError && (
            <div className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-destructive">
                KPI temporaneamente non disponibili. La tabella componenti resta operativa.
                {lastValidValoreVista ? ' Sono mostrati gli ultimi valori disponibili.' : ''}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => refetchValoreVista()}>
                Riprova KPI
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : componentiFiltrati.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun componente trovato</p>
              <p className="text-sm mt-1">
                {hasActiveFilters ? 'Prova a modificare i filtri di ricerca' : 'Inizia aggiungendo il primo componente'}
              </p>
              {!hasActiveFilters && (
              <Button asChild className="mt-4" variant="outline">
                <Link to="/box/componenti/nuovo">Crea il primo componente</Link>
              </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead field="codice" currentOrdering={ordering} onSort={setOrdering}>
                    Codice
                  </SortableTableHead>
                  <SortableTableHead field="nome" currentOrdering={ordering} onSort={setOrdering}>
                    Nome
                  </SortableTableHead>
                  <SortableTableHead field="tipo" currentOrdering={ordering} onSort={setOrdering}>
                    Tipo
                  </SortableTableHead>
                  <SortableTableHead field="costo_cents" currentOrdering={ordering} onSort={setOrdering} className="text-right">
                    Costo
                  </SortableTableHead>
                  <SortableTableHead field="dimensioni" currentOrdering={ordering} onSort={setOrdering}>Dimensioni</SortableTableHead>
                  <SortableTableHead field="giacenza_disponibile" currentOrdering={ordering} onSort={setOrdering} className="text-center">Disp</SortableTableHead>
                  <SortableTableHead field="giacenza_riservata" currentOrdering={ordering} onSort={setOrdering} className="text-center">Ris</SortableTableHead>
                  <SortableTableHead field="giacenza_effettiva" currentOrdering={ordering} onSort={setOrdering} className="text-center">Eff</SortableTableHead>
                  <SortableTableHead field="soglia_minima" currentOrdering={ordering} onSort={setOrdering} className="text-center">Soglia</SortableTableHead>
                  <SortableTableHead field="stock_status" currentOrdering={ordering} onSort={setOrdering} className="text-center">Stock</SortableTableHead>
                  <SortableTableHead field="attivo" currentOrdering={ordering} onSort={setOrdering} className="text-center">Stato</SortableTableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {componentiFiltrati.map((componente) => {
                  const giacenza = giacenzeByComponente.get(componente.id)
                  return (
                    <TableRow key={componente.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell className="font-mono text-sm">
                      <Link to={`/box/componenti/${componente.id}/modifica`} className="hover:underline">
                        {componente.codice}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link to={`/box/componenti/${componente.id}/modifica`} className="hover:underline">
                        {componente.nome}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge className={getTipoBadgeColor(componente.tipo)}>
                        {componente.tipo_display || componente.tipo}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(componente.costo_cents)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {componente.dimensioni || '-'}
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {giacenza ? giacenza.quantita_disponibile : '-'}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">
                      {giacenza ? giacenza.quantita_riservata : '-'}
                    </TableCell>
                    <TableCell className="text-center font-semibold">
                      {giacenza ? giacenza.quantita_effettiva : '-'}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">
                      {giacenza ? giacenza.soglia_minima : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {getStockBadge(giacenza)}
                    </TableCell>
                    <TableCell className="text-center">
                      {componente.attivo ? (
                        <Badge variant="success">Attivo</Badge>
                      ) : (
                        <Badge variant="secondary">Inattivo</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" title="Modifica componente" aria-label="Modifica componente" asChild>
                          <Link to={`/box/componenti/${componente.id}/modifica`}>
                            <PencilSquareIcon className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Modifica giacenza"
                          aria-label="Modifica giacenza"
                          onClick={() => openStockDialog(componente)}
                        >
                          <BuildingStorefrontIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          title="Elimina componente"
                          aria-label="Elimina componente"
                          onClick={() => setDeleteDialog({ open: true, componente })}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Conferma Eliminazione */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, componente: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare il componente &quot;{deleteDialog.componente?.nome}&quot;?
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, componente: null })}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate(deleteDialog.componente?.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={stockDialog.open}
        onOpenChange={(open) => setStockDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Giacenza Componente</DialogTitle>
            <DialogDescription>
              Aggiorna rapidamente la giacenza di {stockDialog.componente?.nome}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="stock-componente-quantita">Quantità disponibile</Label>
              <Input
                id="stock-componente-quantita"
                type="number"
                min="0"
                value={stockForm.quantita_disponibile}
                onChange={(e) => setStockForm((prev) => ({ ...prev, quantita_disponibile: e.target.value }))}
                placeholder="Quantità disponibile"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stock-componente-soglia">Soglia minima</Label>
              <Input
                id="stock-componente-soglia"
                type="number"
                min="0"
                value={stockForm.soglia_minima}
                onChange={(e) => setStockForm((prev) => ({ ...prev, soglia_minima: e.target.value }))}
                placeholder="Soglia minima"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stock-componente-ubicazione">Ubicazione</Label>
              <Input
                id="stock-componente-ubicazione"
                value={stockForm.ubicazione}
                onChange={(e) => setStockForm((prev) => ({ ...prev, ubicazione: e.target.value }))}
                placeholder="Ubicazione (opzionale)"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Quantità riservata attuale: {stockDialog.giacenza?.quantita_riservata ?? 0}
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStockDialog({ open: false, componente: null, giacenza: null })}
            >
              Annulla
            </Button>
            <Button onClick={handleStockSubmit} disabled={updateStockMutation.isPending}>
              {updateStockMutation.isPending ? 'Salvataggio...' : 'Salva Giacenza'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ListPagination data={componentiData?.data} page={page} setPage={setPage} isFetching={isFetching} error={error} />
    </div>
  )
}
